// Platform keys (docs/12 "Users, invitations and personal tenancy", D55): the platform
// organisation's credentials, such as the default model key a task group uses when its owner
// binds none. Metadata only; adding and deleting are signed by this browser's device key.
import { Key, Plus, Trash, Warning } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { ActionIcon, Button } from "rizzui";

import { useDevice } from "../device/context";
import { type Messages, useT } from "../i18n";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { toast } from "../layout/toast";
import { AddPlatformKeyDrawer } from "../platform/AddPlatformKeyDrawer";
import { deletePlatformKey, platformCredentialsQuery } from "../platform/api";
import type { Signer } from "../secrets/requests";
import { type Credential, templateOf } from "../secrets/templates";
import { cardClass, IconTile } from "../ui/Card";
import { cn } from "../ui/cn";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { type Column, DataTable } from "../ui/DataTable";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { Mono, Pill } from "../ui/status";
import { ago } from "./format";

function columns(t: Messages["secrets"], remove?: (c: Credential) => void): Column<Credential>[] {
  return [
    {
      key: "name",
      header: t.table.name,
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-mono text-[13px] font-semibold text-gray-900">{c.name}</div>
          <div className="text-xs text-gray-500">{templateOf(c)?.label ?? c.kind}</div>
        </div>
      ),
    },
    { key: "kind", header: t.platform.kind, hideBelow: "md", cell: (c) => <Mono>{c.kind}</Mono> },
    { key: "kept", header: t.table.kept, hideBelow: "lg", cell: () => <Pill tone="secondary">{t.table.holder.server}</Pill> },
    { key: "used", header: t.table.lastUsed, hideBelow: "sm", cell: (c) => (c.last_used_at ? ago(c.last_used_at) : t.table.never) },
    {
      key: "actions",
      header: "",
      className: "text-end",
      cell: (c) =>
        remove ? (
          <ActionIcon size="sm" variant="outline" aria-label={t.table.deleteLabel(c.name)} className="hover:border-red hover:text-red" onClick={() => remove(c)}>
            <Trash weight="bold" className="h-4 w-4" />
          </ActionIcon>
        ) : null,
    },
  ];
}

export default function PlatformKeysPage() {
  const { common, secrets: t } = useT();
  const crumbs = sectionCrumbs("platformKeys");
  const query = useQuery(platformCredentialsQuery());
  const queryClient = useQueryClient();
  const device = useDevice();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Credential>();
  const signer: Signer | undefined =
    device.kind === "linked" ? { proto: device.proto, deviceId: device.device.deviceId, key: device.device.keys.auth.privateKey } : undefined;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["platform-credentials"] });
  const orgId = query.data?.org_id;

  return (
    <>
      <PageHeader title={SECTIONS.platformKeys.title} breadcrumb={crumbs} description={SECTIONS.platformKeys.subtitle}>
        {signer && orgId ? (
          <Button className="gap-1.5" onClick={() => setAdding(true)}>
            <Plus weight="bold" className="h-4 w-4" /> {t.platform.add}
          </Button>
        ) : null}
      </PageHeader>
      {device.kind === "unlinked" ? (
        <section className={cn(cardClass, "mb-5 flex items-start gap-3 p-5")} aria-label={t.page.unlinkedLabel}>
          <IconTile className="bg-orange-lighter text-orange-dark">
            <Warning weight="duotone" />
          </IconTile>
          <p className="m-0 text-[13px] text-gray-500">
            {t.platform.unlinked(
              <Link to="/devices" className="font-medium text-primary">
                {t.platform.linkBrowser}
              </Link>,
            )}
          </p>
        </section>
      ) : null}
      <div className={cn(cardClass, "p-3 lg:p-4")}>
        {query.isPending ? <Loading /> : null}
        {query.isError ? <ErrorState title={t.platform.loadError} message={query.error.message} /> : null}
        {query.data?.credentials.length === 0 ? (
          <EmptyState compact icon={<Key weight="duotone" />} title={t.platform.empty}>
            {t.platform.emptyBody}
          </EmptyState>
        ) : null}
        {query.data && query.data.credentials.length > 0 ? (
          <DataTable label={t.platform.tableLabel} columns={columns(t, signer ? setDeleting : undefined)} rows={query.data.credentials} rowKey={(c) => c.id} />
        ) : null}
      </div>
      {signer && orgId ? (
        <AddPlatformKeyDrawer
          open={adding}
          onClose={() => setAdding(false)}
          signer={signer}
          signerName={device.kind === "linked" ? device.device.name : ""}
          orgId={orgId}
          onError={(m) => toast(m, "warn")}
          onSaved={(c) => {
            void refresh();
            toast(t.page.saved(c.name));
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== undefined}
        label={t.platform.deleteQuestion(deleting?.name ?? "")}
        title={t.platform.deleteQuestion(deleting?.name ?? "")}
        confirmLabel={common.actions.delete}
        onClose={() => setDeleting(undefined)}
        onConfirm={async () => {
          if (!deleting || !signer) return;
          await deletePlatformKey(signer, deleting.id);
          await refresh();
          toast(t.page.deleted(deleting.name));
        }}
      >
        {t.platform.deleteBody}
      </ConfirmDialog>
    </>
  );
}
